import type { RouteRecordRaw } from 'vue-router'
import type { SettingsConfig } from './config'

/**
 * The account and organization settings, each gated on the permission the server checks behind
 * it (`meta.requires`, read by the permission guard).
 *
 * One screen of the organization is not registered: its account (profile, contact, legal,
 * branding, billing profile). No route serves it, and `AccountView` stays in the tree,
 * unreachable, until that port lands.
 */
export function createSettingsRoutes(config: SettingsConfig): RouteRecordRaw[] {
  const integrated = !!config.parentRouteName
  const children: RouteRecordRaw[] = []

  // In integrated mode, organizationId comes from parent route (/org/:organizationId)
  // In standalone mode, it's part of the child path
  const orgPrefix = integrated ? '' : 'organization/:organizationId/'

  if (config.features.profile) {
    children.push({
      path: `${orgPrefix}profile`,
      name: 'settings:profile',
      component: () => import('./views/ProfileView.vue'),
      meta: {
        title: 'Profile',
        requiresAuth: true,
      },
    })
  }

  if (config.features.apiTokens) {
    children.push({
      path: 'api-tokens',
      name: 'settings:api-tokens',
      component: () => import('./views/ApiTokensView.vue'),
      meta: {
        title: 'API Tokens',
        requiresAuth: true,
        requires: { permissions: 'auth.api_keys.manage' },
      },
    })
  }

  // The roster is open to every member, as its route is, so neither screen names a permission:
  // the invitations tab is gated on `auth.members.invite` inside the list, and a member's page
  // offers its writes to `auth.members.manage`. The page reads its row out of the same roster.
  //
  // The membership requests are the same screen's third tab, with an address of its own because
  // the reviewers' mail links to it; the static `requests` segment outranks `:memberId`. Both of
  // its routes take `tenancy.membership_requests.review`, the key every read behind them asks.
  if (config.features.organizationMembers) {
    children.push(
      {
        path: `${orgPrefix}members`,
        name: 'settings:organization-members',
        component: () => import('./views/MembersView.vue'),
        meta: {
          title: 'Members',
          requiresAuth: true,
        },
      },
      {
        path: `${orgPrefix}members/requests`,
        name: 'settings:organization-membership-requests',
        component: () => import('./views/MembersView.vue'),
        meta: {
          title: 'Membership requests',
          requiresAuth: true,
          requires: { permissions: 'tenancy.membership_requests.review' },
        },
      },
      {
        path: `${orgPrefix}members/requests/:requestId`,
        name: 'settings:organization-membership-request-detail',
        component: () => import('./views/MembershipRequestDetailView.vue'),
        meta: {
          title: 'Membership request',
          requiresAuth: true,
          requires: { permissions: 'tenancy.membership_requests.review' },
        },
      },
      {
        path: `${orgPrefix}members/:memberId`,
        name: 'settings:organization-member-detail',
        component: () => import('./views/MemberDetailView.vue'),
        meta: {
          title: 'Member',
          requiresAuth: true,
        },
      },
    )
  }

  // Organization roles are written with `auth.roles.manage`, workspace roles with
  // `tenancy.settings.manage`; either opens the list, where each half gates its own writes.
  if (config.features.organizationRoles) {
    children.push(
      {
        path: `${orgPrefix}roles`,
        name: 'settings:organization-roles',
        component: () => import('./views/RolesView.vue'),
        meta: {
          title: 'Roles & Permissions',
          requiresAuth: true,
          requires: { anyPermissions: ['auth.roles.manage', 'tenancy.settings.manage'] },
        },
      },
      {
        path: `${orgPrefix}roles/create`,
        name: 'settings:organization-role-create',
        component: () => import('./views/RoleCreateView.vue'),
        meta: {
          title: 'Create Role',
          requiresAuth: true,
          requires: { permissions: 'auth.roles.manage' },
        },
      },
      {
        path: `${orgPrefix}roles/:roleKey`,
        name: 'settings:organization-role-detail',
        component: () => import('./views/RoleDetailView.vue'),
        meta: {
          title: 'Role Detail',
          requiresAuth: true,
          requires: { anyPermissions: ['auth.roles.manage', 'tenancy.settings.manage'] },
        },
      },
      {
        path: `${orgPrefix}workspace-roles/create`,
        name: 'settings:organization-workspace-role-create',
        component: () => import('./views/WorkspaceRoleCreateView.vue'),
        meta: {
          title: 'Create Workspace Role',
          requiresAuth: true,
          requires: { permissions: 'tenancy.settings.manage' },
        },
      },
      {
        path: `${orgPrefix}workspace-roles/:roleKey`,
        name: 'settings:organization-workspace-role-detail',
        component: () => import('./views/WorkspaceRoleDetailView.vue'),
        meta: {
          title: 'Workspace Role Detail',
          requiresAuth: true,
          requires: { anyPermissions: ['auth.roles.manage', 'tenancy.settings.manage'] },
        },
      },
    )
  }

  if (config.customRoutes) {
    children.push(...config.customRoutes)
  }

  if (config.features.dangerZone) {
    // The organization's: handing it over, and deleting it — each card behind its own permission.
    children.push({
      path: 'danger-zone',
      name: 'settings:danger-zone',
      component: () => import('./views/DangerZoneView.vue'),
      meta: {
        title: 'Danger Zone',
        requiresAuth: true,
        requires: { anyPermissions: ['auth.org.delete', 'auth.org.transfer'] },
      },
    })

    // The account's, opened from the profile.
    children.push({
      path: 'delete-account',
      name: 'settings:delete-account',
      component: () => import('./views/DeleteAccountView.vue'),
      meta: {
        title: 'Delete Account',
        requiresAuth: true,
      },
    })
  }

  // In integrated mode, path is relative (e.g. 'settings')
  // In standalone mode, path is absolute (e.g. '/settings')
  const basePath = integrated ? 'settings' : config.basePath

  const routes: RouteRecordRaw[] = [
    {
      path: basePath,
      name: 'settings:layout',
      component: () => import('./views/SettingsLayout.vue'),
      meta: { requiresAuth: true },
      children,
    },
  ]

  return routes
}
